import { Button, DropdownMenu, Flex } from '@radix-ui/themes'
import { useLocation, useNavigate } from 'react-router';
import { useAtom, useSetAtom } from 'jotai'
import { DexRadixSize } from '#app/config/ui.config.ts'
import {
  globalProjectAtom,
  type GlobalWorkspace,
  globalWorkspaceAtom,
} from '#app/data/states/global-state.ts'
import { useBreakpoints } from '#app/hooks/breakpoints.hook.ts'
import { DexIconOk, DexIconWorkspace } from '../ui/icons/dex-icons.tsx'

type WorkspaceSelectorProps = {
  workspaces: GlobalWorkspace[]
}

export function WorkspaceSelector({ workspaces }: WorkspaceSelectorProps) {
  const [atomWorkspace, setWorkspace] = useAtom(globalWorkspaceAtom)
  const setGlobalProject = useSetAtom(globalProjectAtom)
  const { is3xl } = useBreakpoints()
  const navigate = useNavigate()
  const location = useLocation()

  // Derive current workspace from URL if atom is not yet set
  const workspaceSlugFromUrl = location.pathname.match(
    /^\/workspaces\/([^/]+)/,
  )?.[1]
  const workspaceFromUrl = workspaceSlugFromUrl
    ? workspaces.find(
        w => w.slug === workspaceSlugFromUrl || w.id === workspaceSlugFromUrl,
      )
    : null

  // Use atom workspace if set, otherwise fall back to URL-derived workspace
  const workspace = atomWorkspace ?? workspaceFromUrl

  function handleWorkspaceChange(workspaceSlug: string) {
    // Skip if selecting the already active workspace
    if (workspace?.slug === workspaceSlug) return

    const selectedWorkspace = workspaces.find(w => w.slug === workspaceSlug)
    if (selectedWorkspace) {
      setWorkspace(selectedWorkspace)
      setGlobalProject(null)
      // Navigate to the new workspace's home page using slug
      navigate(`/workspaces/${workspaceSlug}`)
    }
  }

  if (!workspace) return null

  if (workspaces.length > 1) {
    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <Button
            size={is3xl ? DexRadixSize.DEFAULT : DexRadixSize.SMALL}
            variant="soft"
            color="gray"
            data-testid="workspace-selector"
          >
            <DexIconWorkspace />
            {workspace.name}
            <DropdownMenu.TriggerIcon />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          {workspaces.map(w => (
            <DropdownMenu.Item
              key={w.id}
              onSelect={() => handleWorkspaceChange(w.slug)}
            >
              <Flex align="center" gap="2">
                {w.slug === workspace.slug && <DexIconOk />}
                <span className={w.slug !== workspace.slug ? 'ml-4' : ''}>
                  {w.name}
                </span>
              </Flex>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    )
  }

  return (
    <Button
      size={is3xl ? DexRadixSize.DEFAULT : DexRadixSize.SMALL}
      variant="soft"
      color="gray"
    >
      <DexIconWorkspace />
      {workspace.name}
    </Button>
  )
}
